"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  allCategoriesGranted,
  type ConsentCategories,
  type ConsentChoice,
  type ConsentRegion,
  defaultCategories,
  readConsentChoice,
  readConsentRegion,
  writeConsentChoice,
} from "@/lib/cookie-consent";

import { CookieConsentBanner } from "./cookie-consent-banner";
import {
  CookieConsentContext,
  type CookieConsentContextValue,
} from "./cookie-consent-context";
import { CookiePreferencesDialog } from "./cookie-preferences-dialog";

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [region, setRegion] = useState<ConsentRegion>("not-required");
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  // Region + stored choice are only readable client-side. Reading them in a
  // mount effect (rather than during render) keeps SSR output region-agnostic
  // so the banner/link never cause a hydration mismatch.
  useEffect(() => {
    setRegion(readConsentRegion());
    setChoice(readConsentChoice());
    setMounted(true);
  }, []);

  const persist = useCallback((categories: ConsentCategories) => {
    // Record the choice only. Enforcement (SDK opt-outs, clearing the Google +
    // PostHog cookies) lands in a follow-up; nothing here changes tracking yet.
    setChoice(writeConsentChoice(categories));
    setIsPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist(allCategoriesGranted());
  }, [persist]);
  const rejectAll = useCallback(() => {
    persist(defaultCategories());
  }, [persist]);
  const saveChoice = useCallback(
    (categories: ConsentCategories) => {
      persist(categories);
    },
    [persist],
  );
  const openPreferences = useCallback(() => {
    setIsPreferencesOpen(true);
  }, []);
  const closePreferences = useCallback(() => {
    setIsPreferencesOpen(false);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      mounted,
      region,
      choice,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      rejectAll,
      saveChoice,
    }),
    [
      mounted,
      region,
      choice,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      rejectAll,
      saveChoice,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieConsentBanner />
      <CookiePreferencesDialog />
    </CookieConsentContext.Provider>
  );
}
